import React, { useState } from 'react';
import { auth, googleProvider, database } from '../firebaseConfig';
import { signInWithPopup } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import generateAPIKey from './APIGenerator'; // Import the API generator function
import { writeUserData } from './Users';
import { writeDailySMSCountData } from './Users';
import { writeWelcomeEmail } from './Users';
import { createCredits } from './Credits';
import { Helmet } from 'react-helmet';

const LoginPage = () => {
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showVerticalLogin, setShowVerticalLogin] = useState(false); // State to toggle vertical login
  const [showVerticalCustomLogin, setShowVerticalCustomLogin] = useState(false); // State to toggle vertical custom login
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user exists in the database
      const userRef = ref(database, 'Users/' + user.uid);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        // User doesn't exist, create a new record
        const API_KEY = generateAPIKey();
        try {
          await writeUserData(
            user.uid,
            user.email,
            user.displayName,
            '',
            'Google Sign In',
            Date.now(),
            API_KEY
          );
          await writeDailySMSCountData(
            user.uid
          );

          // Create a record in the Credits table
          try {
            const initialCredits = createCredits(user.uid); // Removed await
          } catch (creditsError) {
            setError(`Failed to create credits record: ${creditsError.message}`);
            return;
          }
          await writeWelcomeEmail(
            user.email
          );
        } catch (userDataError) {
          setError(`Failed to create user record: ${userDataError.message}`);
          return;
        }
      }

      navigate('/DashboardPage', { state: { user: { uid: user.uid, email: user.email, displayName: user.displayName } } });
    } catch (error) {
      setError(`Sign-in error: ${error.message}`);
    }
  };

  const handleCustomSignIn = async () => {
    // Handle custom sign-in logic
  };

  return (
    <div style={containerStyle}>
      {/* Add Helmet to dynamically set title and meta description */}
      <Helmet>
        <title>Login | SMS Textr</title> {/* Set the page title */}
        <meta name="description" content="Login to SMS Textr to send SMS with our easy API. Access your dashboard and manage your SMS credits." />
        <meta name="keywords" content="Login, SMS Textr, SMS API, Authentication" />
        <meta name="author" content="SMS Textr Team" />
      </Helmet>
      <div style={loginContainerStyle}>
        <div style={loginBoxStyle}>
          <h2>Login</h2>
          <button style={googleButtonStyle} onClick={handleGoogleSignIn}>
            Sign In with Google
          </button>
          {error && <p style={{ color: 'red' }}>{error}</p>}
        </div>
        <div style={dividerStyle}></div>
        <div style={customLoginBoxStyle} onClick={handleCustomSignIn}>
          <p style={{ color: 'red' }}>Custom sign-in is currently unavailable. Use Sign in with Google.</p>
          <h2>Login</h2>
          <input 
            type="text" 
            placeholder="Username" 
            value={username} 
            onChange={(e) => setUsername(e.target.value)} 
            style={inputStyle} 
          />
          <input 
            type="password" 
            placeholder="Password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            style={inputStyle} 
          />
          <button style={loginButtonStyle} disabled>Login</button>
          <p style={{ marginTop: '10px' }}>
            Don't have an account? <button style={{ background: 'none', color: '#ccc', border: 'none', cursor: 'not-allowed' }} disabled>Sign Up</button>
          </p>
        </div>
      </div>

      {/* Vertical Login Container (Hidden) */}
      <div style={{ ...verticalLoginContainerStyle, display: showVerticalLogin ? 'block' : 'none' }}>
        <h2>Vertical Login</h2>
        <input 
          type="text" 
          placeholder="Email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          style={inputStyle} 
        />
        <input 
          type="password" 
          placeholder="Password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          style={inputStyle} 
        />
        <button style={loginButtonStyle}>Login</button>
      </div>

      {/* Vertical Custom Login Container (Hidden) */}
      <div style={{ ...verticalCustomLoginContainerStyle, display: showVerticalCustomLogin ? 'block' : 'none' }}>
        <h2>Vertical Custom Login</h2>
        <input 
          type="text" 
          placeholder="Username" 
          value={username} 
          onChange={(e) => setUsername(e.target.value)} 
          style={inputStyle} 
        />
        <input 
          type="password" 
          placeholder="Password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          style={inputStyle} 
        />
        <button style={loginButtonStyle}>Login</button>
      </div>
    </div>
  );
};

// Style definitions
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  backgroundColor: '#f4f4f4',
};

const loginContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#fff',
  padding: '50px',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
};

const loginBoxStyle = {
  width: '100%',
  maxWidth: '300px',
  textAlign: 'center',
};

const googleButtonStyle = {
  backgroundColor: '#4285F4',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '4px',
  cursor: 'pointer',
  width: '100%',
  marginTop: '10px',
};

const dividerStyle = {
  width: '100%',
  height: '1px',
  backgroundColor: '#ddd',
  margin: '10px 0',
};

const customLoginBoxStyle = {
  width: '100%',
  maxWidth: '300px',
  textAlign: 'center',
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  marginBottom: '10px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  fontSize: '16px',
};

const loginButtonStyle = {
  width: '100%',
  padding: '10px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '16px',
};

const verticalLoginContainerStyle = {
  display: 'none',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  textAlign: 'center',
  width: '100%',
  maxWidth: '280px',
  margin: '20px auto',
};

const verticalCustomLoginContainerStyle = {
  display: 'none',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  textAlign: 'center',
  width: '100%',
  maxWidth: '280px',
  margin: '20px auto',
};

export default LoginPage;
